import { message } from 'antd'
import store from '../../../../store'
import dayjs from 'dayjs'
import Agilite from 'agilite'
import BillingEnums from '../../utils/enums'
import {
  createLedgerTransaction,
  recordPayment,
  recordRemittancePayment,
  recordRefund,
  recordCredit,
  recordDebit
} from './patientLedger'
import { getFollowUpAccounts, updateFollowUpAccountById } from './followUpAccounts'
import { logAuditEvent, AuditActions, AuditEntityTypes } from './auditTrail'

const agilite = new Agilite({
  apiServerUrl: process.env.REACT_APP_AGILITE_API_URL,
  apiKey: process.env.REACT_APP_AGILITE_API_KEY
})

/**
 * Process invoice creation and create corresponding ledger entry
 * @param {Object} invoiceData - Invoice data from invoice creation
 * @returns {Promise} Result of ledger transaction creation
 */
export const processInvoiceCreation = async (invoiceData) => {
  try {
    console.log('Processing invoice creation:', {
      invoiceId: invoiceData._id,
      invoiceNo: invoiceData.invoiceNo,
      billingRecordsCount: invoiceData.billingRecords?.length || 0
    })

    // Extract billing records to calculate amounts
    const { billingRecords, patientRecord, invoiceNo, _id: invoiceId } = invoiceData

    let totalAmount = 0
    let medicalAidAmount = 0
    let patientAmount = 0

    // Calculate totals from billing records
    if (billingRecords && billingRecords.length > 0) {
      console.log('Processing billing records for invoice:', invoiceNo)
      billingRecords.forEach((record, index) => {
        // Handle both field name variations
        const recordTotal = parseFloat(record.total || 0)
        const recordMedicalAid = parseFloat(record.medicalAid || record.medicalAidDue || 0)
        const recordPatient = parseFloat(record.patient || record.patientDue || 0)

        console.log(`Billing record ${index + 1}:`, {
          type: record.type,
          code: record.code,
          name: record.name,
          qty: record.qty,
          total: recordTotal,
          medicalAid: recordMedicalAid,
          patient: recordPatient,
          originalTotal: record.total,
          originalMedicalAid: record.medicalAid,
          originalPatient: record.patient,
          fullRecord: record
        })

        totalAmount += recordTotal
        medicalAidAmount += recordMedicalAid
        patientAmount += recordPatient
      })
    } else {
      console.warn('No billing records found for invoice:', invoiceId)
      // If no billing records, return without creating ledger entry
      return {
        success: false,
        error: 'No billing records found',
        totals: {
          total: 0,
          medicalAid: 0,
          patient: 0
        }
      }
    }

    console.log('Calculated totals:', {
      totalAmount,
      medicalAidAmount,
      patientAmount
    })

    // Get patient's billingId
    let billingId = null
    if (patientRecord?.billingId) {
      billingId = patientRecord.billingId
    } else if (invoiceData.billingId) {
      billingId = invoiceData.billingId
    } else {
      // Try to get patient data to find billingId
      try {
        const patientData = await agilite.Connectors.execute('system_users', BillingEnums.routeKeys.FIND_BY_ID, {
          id: invoiceData.userRef
        })
        if (patientData?.data?.billingId) {
          billingId = patientData.data.billingId
        }
      } catch (err) {
        console.error('Could not fetch patient billing ID:', err)
      }
    }

    // billingId is required - throw error if not found
    if (!billingId) {
      throw new Error('Patient does not have a billingId. Cannot process invoice without billing account.')
    }

    // Create ledger transaction for the invoice
    const ledgerTransaction = {
      patientId: invoiceData.userRef,
      billingId: billingId,
      date: invoiceData.bookingDate || new Date(),
      type: BillingEnums.transactionTypes.INVOICE,
      description: `Invoice ${invoiceNo}`,
      invoiceNo: invoiceNo,
      invoiceRef: invoiceId,
      debit: totalAmount,
      credit: 0,
      status: 'Pending',
      medicalAidAmount: medicalAidAmount,
      patientAmount: patientAmount,
      notes: `Invoice created - Medical Aid: R${medicalAidAmount.toFixed(2)}, Patient: R${patientAmount.toFixed(2)}`
    }

    console.log('Creating ledger transaction:', ledgerTransaction)

    const result = await createLedgerTransaction(ledgerTransaction)

    // Check for active (non-completed) follow-up accounts
    const accounts = await getFollowUpAccounts(
      {
        patientId: invoiceData.userRef,
        billingId: billingId,
        status: { $ne: 'completed' } // Exclude completed accounts
      },
      1,
      1
    )

    if (accounts && accounts.length > 0) {
      // Update existing active account
      const account = accounts[0]
      console.log('Updating existing follow-up account:', account._id)
      await updateFollowUpAccountById(account._id, {
        outstandingAmount: account.outstandingAmount + totalAmount,
        medicalAidOutstanding: account.medicalAidOutstanding + medicalAidAmount,
        patientOutstanding: account.patientOutstanding + patientAmount,
        lastInvoiceDate: new Date()
      })
    } else {
      // Create new follow-up account if none exists or all are completed
      console.log('Creating new follow-up account for patient:', invoiceData.userRef)

      // Get patient details for the account
      const { findAgiliteUser } = await import('../../../Auth/utils/utils')
      const patient = await findAgiliteUser({ _id: invoiceData.userRef })

      if (patient) {
        // Use the same billingId that was used for the ledger transaction
        const newAccount = {
          billingId: billingId,
          billindId: billingId, // TEMPORARY: Remove this after fixing MongoDB index
          accountNo: billingId,
          patientId: invoiceData.userRef,
          patientName: `${patient.firstName} ${patient.lastName}`,
          fileNo: patient.fileNo || '',
          medicalAid: patient.medicalAid?.name || '',
          outstandingAmount: totalAmount,
          patientOutstanding: patientAmount,
          medicalAidOutstanding: medicalAidAmount,
          daysOutstanding: 0,
          agingBucket: '0-29',
          status: medicalAidAmount > 0 ? 'awaiting_payment_aid' : 'awaiting_payment_patient',
          accountGroup: patient.medicalAid?.name ? 'medical_aid' : 'private',
          lastInvoiceDate: new Date(),
          nextFollowUpDate: dayjs().add(7, 'day').toDate(),
          remittanceStatus: medicalAidAmount > 0 ? 'pending' : 'paid',
          followUpHistory: [],
          statusHistory: [
            {
              status: 'created',
              date: new Date().toISOString(),
              user: store.getState().auth.agiliteUser?._id,
              notes: 'Account created from invoice',
              invoiceRef: invoiceId
            }
          ],
          entityRef: store.getState().core.entity._id,
          createdBy: store.getState().auth.agiliteUser?._id,
          createdAt: new Date()
        }

        // Import the create function
        const { createFollowUpAccount } = await import('./followUpAccounts')
        await createFollowUpAccount(newAccount)

        console.log('Follow-up account created successfully')
      } else {
        console.error('Could not find patient details for follow-up account creation')
      }
    }

    // Log audit trail for invoice creation
    await logAuditEvent(
      AuditActions.CREATE,
      AuditEntityTypes.INVOICE,
      invoiceId,
      `Invoice ${invoiceNo} created for R${totalAmount.toFixed(2)}`,
      {
        invoiceNo,
        totalAmount,
        medicalAidAmount,
        patientAmount,
        patientId: invoiceData.userRef,
        billingId
      }
    )

    return {
      success: true,
      ledgerEntry: result,
      totals: {
        total: totalAmount,
        medicalAid: medicalAidAmount,
        patient: patientAmount
      }
    }
  } catch (error) {
    console.error('Error processing invoice creation:', error)
    throw error
  }
}

/**
 * Process patient payment and update ledger
 * @param {Object} paymentData - Payment data from patient payment form
 * @returns {Promise} Result of payment processing
 */
export const processPatientPayment = async (paymentData) => {
  try {
    const { invoiceRef, userRef: patientId, paymentAmount, paymentMethod, receiptNumber, items } = paymentData

    // Validate EFT transactions have a reference
    if (paymentMethod === BillingEnums.paymentMethods.EFT && (!receiptNumber || receiptNumber.trim().length === 0)) {
      throw new Error('EFT payments must include a reference number')
    }

    // Get patient's billingId for validation
    const patientData = await agilite.Connectors.execute('system_users', BillingEnums.routeKeys.FIND_BY_ID, {
      id: patientId
    })
    let billingId = patientData?.data?.billingId
    if (!billingId) {
      throw new Error('Patient does not have a billingId. Cannot process payment without billing account.')
    }

    // Validate payment amount against outstanding patient balance to prevent overpayment
    const paymentAmountFloat = parseFloat(paymentAmount) || 0
    if (paymentAmountFloat > 0) {
      const { getPatientLedgerSummary } = await import('./patientLedger')
      const balanceSummary = await getPatientLedgerSummary(billingId, patientId)

      if (balanceSummary) {
        const patientBalance = balanceSummary.patientBalance || 0
        const medicalAidBalance = balanceSummary.medicalAidBalance || 0

        // Check if patient payment exceeds patient balance when medical aid balance exists
        if (paymentAmountFloat > patientBalance && medicalAidBalance > 0) {
          throw new Error(
            `Patient payment of R${paymentAmountFloat.toFixed(
              2
            )} exceeds patient outstanding balance of R${patientBalance.toFixed(2)}. ` +
              `Medical aid balance of R${medicalAidBalance.toFixed(2)} is still outstanding. ` +
              `Maximum patient payment allowed: R${patientBalance.toFixed(2)}`
          )
        }
      }
    }

    // Calculate how much of the payment applies to patient vs medical aid portions
    let patientPortionPaid = 0
    let medicalAidPortionPaid = 0

    if (items && items.length > 0) {
      items.forEach((item) => {
        patientPortionPaid += parseFloat(item.payment || 0)
        // If payment reduces medical aid amount, track it
        if (item.oldMedicalAid && parseFloat(item.medicalAid) < parseFloat(item.oldMedicalAid)) {
          medicalAidPortionPaid += parseFloat(item.oldMedicalAid) - parseFloat(item.medicalAid)
        }
      })
    }

    // Record the payment in ledger
    const ledgerResult = await recordPayment({
      patientId,
      billingId: billingId,
      accountNo: billingId,
      invoiceNo: paymentData.invoiceNo,
      amount: paymentAmount,
      paymentMethod,
      reference: receiptNumber,
      isMedicalAid: false,
      notes: `Patient payment via ${paymentMethod}`
    })

    // Update active follow-up account (exclude completed accounts)
    const accounts = await getFollowUpAccounts(
      {
        patientId: patientId,
        billingId: billingId,
        status: { $ne: 'completed' } // Only update active accounts
      },
      1,
      1
    )

    if (accounts && accounts.length > 0) {
      const account = accounts[0]
      const newOutstandingAmount = Math.max(0, account.outstandingAmount - parseFloat(paymentAmount))
      const newPatientOutstanding = Math.max(0, account.patientOutstanding - patientPortionPaid)

      // Determine if account should be marked as completed
      const newStatus = newOutstandingAmount === 0 ? 'completed' : account.status

      await updateFollowUpAccountById(account._id, {
        patientOutstanding: newPatientOutstanding,
        outstandingAmount: newOutstandingAmount,
        lastPaymentDate: new Date(),
        lastPaymentAmount: parseFloat(paymentAmount),
        status: newStatus
      })
    }

    // Log audit trail for patient payment
    await logAuditEvent(
      AuditActions.PAYMENT,
      AuditEntityTypes.PAYMENT,
      ledgerResult._id,
      `Patient payment of R${paymentAmount} processed for invoice ${paymentData.invoiceNo || 'N/A'}`,
      {
        amount: parseFloat(paymentAmount),
        paymentMethod,
        receiptNumber,
        patientId,
        billingId: paymentData.billingId, // Link to follow-up account
        invoiceNo: paymentData.invoiceNo,
        patientPortionPaid,
        medicalAidPortionPaid
      }
    )

    return {
      success: true,
      ledgerEntry: ledgerResult,
      amountApplied: {
        total: parseFloat(paymentAmount),
        patientPortion: patientPortionPaid,
        medicalAidPortion: medicalAidPortionPaid
      }
    }
  } catch (error) {
    console.error('Error processing patient payment:', error)
    throw error
  }
}

/**
 * Process medical aid remittance and update ledger
 * @param {Object} remittanceData - Remittance data with payments
 * @returns {Promise} Result of remittance processing
 */
export const processMedicalAidRemittance = async (remittanceData) => {
  try {
    const results = []
    const { payments, medicalAid, receiptNumber, remittanceDate, _id: remittanceId } = remittanceData

    for (const payment of payments) {
      try {
        // Validate that we're not allocating to a private invoice
        if (payment.invoiceRef) {
          const invoiceData = await agilite.Connectors.execute('invoices', BillingEnums.routeKeys.FIND_BY_ID, {
            id: payment.invoiceRef
          })

          if (invoiceData?.data?.accountGroup === 'private') {
            throw new Error(
              `Medical aid remittance cannot be allocated to private invoice ${payment.invoiceNo}. Account group: ${invoiceData.data.accountGroup}`
            )
          }
        }

        // Record remittance payment in ledger
        const ledgerResult = await recordRemittancePayment({
          patientId: payment.userRef,
          invoiceNo: payment.invoiceNo,
          amount: payment.paymentAmount,
          medicalAid,
          receiptNumber,
          remittanceDate,
          remittanceId
        })

        // Update active follow-up account (exclude completed accounts)
        const accounts = await getFollowUpAccounts(
          {
            patientId: payment.userRef,
            billingId: payment.billingId,
            status: { $ne: 'completed' } // Only update active accounts
          },
          1,
          1
        )

        if (accounts && accounts.length > 0) {
          const account = accounts[0]
          const paymentAmount = parseFloat(payment.paymentAmount)
          const newMedicalAidOutstanding = Math.max(0, account.medicalAidOutstanding - paymentAmount)
          const newOutstandingAmount = Math.max(0, account.outstandingAmount - paymentAmount)

          // Determine if account should be marked as completed
          const newStatus = newOutstandingAmount === 0 ? 'completed' : account.status

          await updateFollowUpAccountById(account._id, {
            medicalAidOutstanding: newMedicalAidOutstanding,
            outstandingAmount: newOutstandingAmount,
            lastRemittanceDate: remittanceDate,
            lastRemittanceAmount: paymentAmount,
            remittanceStatus: newMedicalAidOutstanding <= 0 ? 'paid' : 'partial',
            status: newStatus
          })

          // Log individual payment audit trail with specific billingId
          await logAuditEvent(
            AuditActions.PAYMENT,
            AuditEntityTypes.PAYMENT,
            ledgerResult._id,
            `Medical aid payment of R${paymentAmount} received for invoice ${payment.invoiceNo}`,
            {
              amount: paymentAmount,
              medicalAid,
              receiptNumber,
              remittanceDate,
              patientId: payment.userRef,
              billingId: account._id, // Link to the specific follow-up account
              invoiceNo: payment.invoiceNo,
              invoiceRef: payment.invoiceRef,
              newAccountStatus: newStatus,
              remainingBalance: newOutstandingAmount
            }
          )
        }

        results.push({
          success: true,
          invoiceRef: payment.invoiceRef,
          patientId: payment.userRef,
          amount: payment.paymentAmount,
          ledgerEntry: ledgerResult
        })
      } catch (error) {
        results.push({
          success: false,
          invoiceRef: payment.invoiceRef,
          patientId: payment.userRef,
          error: error.message
        })
      }
    }

    // Log audit trail for remittance processing
    // For remittances affecting multiple billing accounts, log a general entry
    // Individual payment entries are logged separately in processMedicalAidPayment
    await logAuditEvent(
      AuditActions.PAYMENT,
      AuditEntityTypes.PAYMENT,
      remittanceId,
      `Medical aid remittance processed: ${results.filter((r) => r.success).length} payments totaling R${payments
        .reduce((sum, p) => sum + parseFloat(p.paymentAmount), 0)
        .toFixed(2)}`,
      {
        medicalAid,
        receiptNumber,
        remittanceDate,
        totalPayments: results.filter((r) => r.success).length,
        failedPayments: results.filter((r) => !r.success).length,
        totalAmount: payments.reduce((sum, p) => sum + parseFloat(p.paymentAmount), 0),
        // Note: Individual billingIds are logged in processMedicalAidPayment for each payment
        affectedBillingIds: [...new Set(payments.map((p) => p.billingId).filter(Boolean))]
      }
    )

    return {
      success: true,
      processed: results.filter((r) => r.success).length,
      failed: results.filter((r) => !r.success).length,
      results
    }
  } catch (error) {
    console.error('Error processing medical aid remittance:', error)
    throw error
  }
}

/**
 * Process refund and update ledger
 * @param {Object} refundData - Refund details
 * @returns {Promise} Result of refund processing
 */
export const processRefund = async (refundData) => {
  try {
    // Record refund in ledger
    const ledgerResult = await recordRefund(refundData)

    // Update follow-up account
    const accounts = await getFollowUpAccounts(
      { patientId: refundData.patientId, billingId: refundData.billingId },
      1,
      1
    )

    if (accounts && accounts.length > 0) {
      const account = accounts[0]
      const refundAmount = parseFloat(refundData.amount)

      // Determine which portion to increase based on refund type
      if (refundData.refundType === 'medical_aid') {
        await updateFollowUpAccountById(account._id, {
          medicalAidOutstanding: account.medicalAidOutstanding + refundAmount,
          outstandingAmount: account.outstandingAmount + refundAmount
        })
      } else {
        await updateFollowUpAccountById(account._id, {
          patientOutstanding: account.patientOutstanding + refundAmount,
          outstandingAmount: account.outstandingAmount + refundAmount
        })
      }
    }

    return {
      success: true,
      ledgerEntry: ledgerResult
    }
  } catch (error) {
    console.error('Error processing refund:', error)
    throw error
  }
}

/**
 * Process credit note and update ledger
 * @param {Object} creditData - Credit note details
 * @returns {Promise} Result of credit processing
 */
export const processCreditNote = async (creditData) => {
  try {
    // Record credit in ledger
    const ledgerResult = await recordCredit(creditData)

    // Update follow-up account
    const accounts = await getFollowUpAccounts(
      { patientId: creditData.patientId, billingId: creditData.billingId },
      1,
      1
    )

    if (accounts && accounts.length > 0) {
      const account = accounts[0]
      const creditAmount = parseFloat(creditData.amount)

      // Credits reduce what's owed
      await updateFollowUpAccountById(account._id, {
        outstandingAmount: Math.max(0, account.outstandingAmount - creditAmount),
        // Determine which portion based on credit application
        ...(creditData.appliesTo === 'medical_aid'
          ? { medicalAidOutstanding: Math.max(0, account.medicalAidOutstanding - creditAmount) }
          : { patientOutstanding: Math.max(0, account.patientOutstanding - creditAmount) })
      })
    }

    return {
      success: true,
      ledgerEntry: ledgerResult
    }
  } catch (error) {
    console.error('Error processing credit note:', error)
    throw error
  }
}

/**
 * Get comprehensive account balance including all transactions
 * @param {string} patientId - Patient ID
 * @returns {Promise} Comprehensive balance information
 */
export const getComprehensiveBalance = async (patientId) => {
  try {
    // First, get patient's billingId
    let billingId = null
    try {
      const patientData = await agilite.Connectors.execute('system_users', BillingEnums.routeKeys.FIND_BY_ID, {
        id: patientId
      })
      if (patientData?.data?.billingId) {
        billingId = patientData.data.billingId
      }
    } catch (err) {
      console.warn('Could not fetch patient billing ID:', err)
    }

    // If no billingId, try to get from follow-up account
    if (!billingId) {
      const accounts = await getFollowUpAccounts({ patientId }, 1, 1)
      if (accounts && accounts.length > 0 && accounts[0].billingId) {
        billingId = accounts[0].billingId
      }
    }

    if (!billingId) {
      console.warn('No billingId found for patient:', patientId)
      // Return zero balances if no billingId
      return {
        summary: {
          totalInvoiced: 0,
          totalPatientPayments: 0,
          totalMedicalAidPayments: 0,
          totalCredits: 0,
          totalDebits: 0,
          totalRefunds: 0,
          totalWriteOffs: 0,
          netBalance: 0
        },
        breakdown: {
          whatPatientOwes: 0,
          creditBalance: 0
        }
      }
    }

    // Get all transactions from ledger using billingId
    const filter = {
      billingId: billingId,
      entityRef: store.getState().core.entity._id
    }

    // Calculate separate balances
    const invoiceTotal = await calculateTypeTotal(filter, BillingEnums.transactionTypes.INVOICE, 'debit')
    const patientPayments = await calculateTypeTotal(filter, BillingEnums.transactionTypes.PAYMENT, 'credit')
    const medicalAidPayments = await calculateTypeTotal(
      filter,
      BillingEnums.transactionTypes.MEDICAL_AID_PAYMENT,
      'credit'
    )
    const credits = await calculateTypeTotal(filter, BillingEnums.transactionTypes.CREDIT, 'credit')
    const debits = await calculateTypeTotal(filter, BillingEnums.transactionTypes.DEBIT, 'debit')
    const refunds = await calculateTypeTotal(filter, BillingEnums.transactionTypes.REFUND, 'debit')
    const writeOffs = await calculateTypeTotal(filter, BillingEnums.transactionTypes.WRITE_OFF, 'credit')

    // Calculate net balances
    const totalDebits = invoiceTotal + debits + refunds
    const totalCredits = patientPayments + medicalAidPayments + credits + writeOffs
    const netBalance = totalDebits - totalCredits

    return {
      summary: {
        totalInvoiced: invoiceTotal,
        totalPatientPayments: patientPayments,
        totalMedicalAidPayments: medicalAidPayments,
        totalCredits: credits,
        totalDebits: debits,
        totalRefunds: refunds,
        totalWriteOffs: writeOffs,
        netBalance
      },
      breakdown: {
        whatPatientOwes: netBalance > 0 ? netBalance : 0,
        creditBalance: netBalance < 0 ? Math.abs(netBalance) : 0
      }
    }
  } catch (error) {
    console.error('Error getting comprehensive balance:', error)
    throw error
  }
}

/**
 * Helper function to calculate totals by transaction type
 */
async function calculateTypeTotal(filter, type, field) {
  const Agilite = (await import('agilite')).default
  const agilite = new Agilite({
    apiServerUrl: process.env.REACT_APP_AGILITE_API_URL,
    apiKey: process.env.REACT_APP_AGILITE_API_KEY
  })

  const pipeline = [
    {
      $match: {
        ...filter,
        type
      }
    },
    {
      $group: {
        _id: null,
        total: { $sum: `$${field}` }
      }
    }
  ]

  const response = await agilite.Connectors.execute(
    BillingEnums.profileKeys.PATIENT_LEDGER,
    BillingEnums.routeKeys.AGGREGATE,
    {
      pipeline: JSON.stringify(pipeline)
    }
  )

  return response.data?.[0]?.total || 0
}

/**
 * Check and sync medical aid payment confirmations
 * @param {string} patientId - Patient ID to check payments for
 * @returns {Promise} Result of payment sync
 */
export const syncMedicalAidPayments = async (patientId) => {
  try {
    console.log('Checking for medical aid payment confirmations for patient:', patientId)

    // Get all invoices for this patient with medical aid claims
    const { getInvoices } = await import('../../../Invoices/utils')
    const invoices = await getInvoices({
      userRef: patientId,
      goodXTicketNo: { $exists: true, $ne: null },
      invoiceType: 'medical'
    })

    console.log('Found medical aid invoices:', invoices?.length || 0)

    if (!invoices || invoices.length === 0) {
      console.log('No medical aid invoices found for patient')
      return { success: true, syncedPayments: 0, message: 'No medical aid invoices to check' }
    }

    const syncResults = []
    let alreadyPaidCount = 0
    let pendingCount = 0
    let rejectedCount = 0

    // Check each invoice for payment status
    for (const invoice of invoices) {
      try {
        console.log('Checking payment status for invoice:', invoice.invoiceNo, 'with ticket:', invoice.goodXTicketNo)

        // Check if we already have a medical aid payment for this invoice
        const existingPayments = await agilite.Connectors.execute(
          BillingEnums.profileKeys.PATIENT_LEDGER,
          BillingEnums.routeKeys.READ,
          {
            filter: JSON.stringify({
              invoiceNo: invoice.invoiceNo,
              type: BillingEnums.transactionTypes.MEDICAL_AID_PAYMENT,
              entityRef: store.getState().core.entity._id
            })
          }
        )

        if (existingPayments.data && existingPayments.data.length > 0) {
          console.log('Medical aid payment already recorded for invoice:', invoice.invoiceNo)
          alreadyPaidCount++
          syncResults.push({
            invoiceNo: invoice.invoiceNo,
            status: 'already_paid',
            existingPayment: existingPayments.data[0]
          })
          continue
        }

        // Get GoodX response to check payment status
        const { goodXResponse, goodXStatus } = await import('../../../Bookings/utils/utils')

        // First check the status
        const status = await goodXStatus(invoice.goodXTicketNo)
        console.log('GoodX status for ticket', invoice.goodXTicketNo, ':', status)

        // Get full response for payment details
        const response = await goodXResponse(invoice.goodXTicketNo)

        if (response?.ProviderClaimResponse?.CommonProviderClaimResponseInfo?.[0]) {
          const claimInfo = response.ProviderClaimResponse.CommonProviderClaimResponseInfo[0]
          const claimStatus = claimInfo.ClaimHeaderStatusCode?.[0]

          console.log('Claim status:', claimStatus)

          // Check if payment has been approved or paid
          if (claimStatus === 'mfAPP' || claimStatus === 'PAID') {
            console.log('Claim approved/paid, looking for payment details...')

            // Check if we already have an approval entry for this invoice
            const existingApprovals = await agilite.Connectors.execute(
              BillingEnums.profileKeys.PATIENT_LEDGER,
              BillingEnums.routeKeys.READ,
              {
                filter: JSON.stringify({
                  invoiceNo: invoice.invoiceNo,
                  type: BillingEnums.transactionTypes.MEDICAL_AID_APPROVAL,
                  entityRef: store.getState().core.entity._id
                })
              }
            )

            // Get payment details from response
            let approvedAmount = 0
            let paidAmount = 0

            // Try to get amounts from claim response
            if (claimInfo.ClaimHeaderFinancial?.[0]?.CommonClaimBaseFinancialInfo?.[0]) {
              const financial = claimInfo.ClaimHeaderFinancial[0].CommonClaimBaseFinancialInfo[0]
              console.log('Financial info found:', {
                approvedAmount: financial.ApprovedClaimAmount?.[0],
                paidAmount: financial.PaidClaimAmount?.[0],
                submittedAmount: financial.SubmittedClaimAmount?.[0],
                fullFinancial: financial
              })
              approvedAmount = parseFloat(financial.ApprovedClaimAmount?.[0] || 0)
              paidAmount = parseFloat(financial.PaidClaimAmount?.[0] || 0)
            }

            // If no amounts in header, sum line items
            if (approvedAmount === 0 && paidAmount === 0 && response.ProviderClaimResponse.ClaimLineItem) {
              console.log('No header amount, checking line items...')
              const lineItems = Array.isArray(response.ProviderClaimResponse.ClaimLineItem)
                ? response.ProviderClaimResponse.ClaimLineItem
                : [response.ProviderClaimResponse.ClaimLineItem]

              lineItems.forEach((item, idx) => {
                if (item.ClaimLineFinancial?.[0]?.CommonClaimBaseFinancialInfo?.[0]) {
                  const lineFinancial = item.ClaimLineFinancial[0].CommonClaimBaseFinancialInfo[0]
                  const lineApproved = parseFloat(lineFinancial.ApprovedClaimAmount?.[0] || 0)
                  const linePaid = parseFloat(lineFinancial.PaidClaimAmount?.[0] || 0)
                  console.log(`Line item ${idx + 1} financial:`, {
                    approvedAmount: lineFinancial.ApprovedClaimAmount?.[0],
                    paidAmount: lineFinancial.PaidClaimAmount?.[0],
                    approved: lineApproved,
                    paid: linePaid
                  })
                  approvedAmount += lineApproved
                  paidAmount += linePaid
                }
              })
            }

            console.log('Total amounts found:', { approved: approvedAmount, paid: paidAmount })

            // Handle approved status (create approval entry if not exists)
            if (claimStatus === 'mfAPP' && approvedAmount > 0 && existingApprovals.data.length === 0) {
              console.log('Creating medical aid approval entry for', invoice.invoiceNo, 'amount:', approvedAmount)

              // Create approval transaction (this is NOT a payment, just tracking)
              const approvalData = {
                patientId: invoice.userRef,
                date: new Date(),
                type: BillingEnums.transactionTypes.MEDICAL_AID_APPROVAL,
                description: `Medical aid claim approved - ${invoice.goodXTicketNo}`,
                invoiceNo: invoice.invoiceNo,
                invoiceRef: invoice._id,
                debit: 0,
                credit: 0, // No ledger impact for approvals
                transactionAmount: 0,
                status: 'Approved',
                medicalAidAmount: approvedAmount,
                patientAmount: 0,
                notes: `Medical aid approved R${approvedAmount.toFixed(2)} - Awaiting payment`,
                reference: invoice.goodXTicketNo,
                entityRef: store.getState().core.entity._id,
                createdBy: store.getState().auth.agiliteUser?._id,
                createdAt: new Date()
              }

              await agilite.Connectors.execute(BillingEnums.profileKeys.PATIENT_LEDGER, BillingEnums.routeKeys.CREATE, {
                data: JSON.stringify(approvalData)
              })

              syncResults.push({
                invoiceNo: invoice.invoiceNo,
                amount: approvedAmount,
                status: 'approved',
                action: 'approval_recorded'
              })
            }

            // Handle paid status (create actual payment)
            if ((claimStatus === 'PAID' || paidAmount > 0) && existingPayments.data.length === 0) {
              const finalAmount = paidAmount > 0 ? paidAmount : approvedAmount
              console.log('Creating medical aid payment for', invoice.invoiceNo, 'amount:', finalAmount)

              // Create medical aid payment transaction
              const paymentData = {
                patientId: invoice.userRef,
                invoiceNo: invoice.invoiceNo,
                amount: finalAmount,
                paymentMethod: 'medical_aid',
                reference: invoice.goodXTicketNo,
                isMedicalAid: true,
                notes: `Medical aid payment confirmed via GoodX - Claim ${claimStatus}`
              }

              await recordPayment(paymentData)

              // Update follow-up account
              const accounts = await getFollowUpAccounts({ patientId: invoice.userRef }, 1, 1)

              if (accounts && accounts.length > 0) {
                const account = accounts[0]
                const remainingMedicalAid = Math.max(0, account.medicalAidOutstanding - finalAmount)

                // Find the original invoice transaction to get the billed amounts
                const originalInvoice = await agilite.Connectors.execute(
                  BillingEnums.profileKeys.PATIENT_LEDGER,
                  BillingEnums.routeKeys.READ,
                  {
                    filter: JSON.stringify({
                      invoiceNo: invoice.invoiceNo,
                      type: BillingEnums.transactionTypes.INVOICE,
                      entityRef: store.getState().core.entity._id
                    })
                  }
                )

                let originalMedicalAidAmount = 0
                if (originalInvoice.data && originalInvoice.data.length > 0) {
                  originalMedicalAidAmount = originalInvoice.data[0].medicalAidAmount || 0
                }

                // Calculate shortfall
                const shortfall = originalMedicalAidAmount - finalAmount
                let patientPortionIncrease = 0
                if (shortfall > 0) {
                  console.log(`Shortfall of R${shortfall.toFixed(2)} detected for invoice ${invoice.invoiceNo}`)
                  patientPortionIncrease = shortfall

                  // Create a debit adjustment to move the shortfall to the patient
                  await createLedgerTransaction({
                    patientId: invoice.userRef,
                    billingId: account.billingId,
                    date: new Date(),
                    type: BillingEnums.transactionTypes.ADJUSTMENT,
                    description: `Medical aid shortfall for Invoice ${invoice.invoiceNo}`,
                    invoiceNo: invoice.invoiceNo,
                    debit: 0, // No impact on overall balance
                    credit: 0,
                    medicalAidAmount: -shortfall, // Decrease medical aid portion
                    patientAmount: shortfall, // Increase patient portion
                    notes: `Shortfall of R${shortfall.toFixed(2)} moved to patient portion.`
                  })
                }

                await updateFollowUpAccountById(account._id, {
                  medicalAidOutstanding: remainingMedicalAid,
                  // Add shortfall to patient's outstanding amount
                  patientOutstanding: account.patientOutstanding + patientPortionIncrease,
                  outstandingAmount: Math.max(0, account.outstandingAmount - finalAmount),
                  lastRemittanceDate: new Date(),
                  lastRemittanceAmount: finalAmount,
                  remittanceStatus: remainingMedicalAid <= 0 ? 'paid' : 'partial',
                  lastActivityDate: new Date()
                })

                // Add to status history
                const statusHistory = account.statusHistory || []
                statusHistory.push({
                  status: account.status,
                  date: new Date().toISOString(),
                  user: store.getState().auth.agiliteUser?._id,
                  notes: `Medical aid payment of R${finalAmount.toFixed(2)} confirmed via GoodX sync`,
                  action: 'payment_confirmed'
                })

                await updateFollowUpAccountById(account._id, { statusHistory })
              }

              syncResults.push({
                invoiceNo: invoice.invoiceNo,
                amount: finalAmount,
                status: 'synced',
                action: 'payment_created'
              })
            } else if (claimStatus === 'mfAPP' && approvedAmount === 0) {
              // Approved but no amount specified
              pendingCount++
              syncResults.push({
                invoiceNo: invoice.invoiceNo,
                status: 'approved_no_amount',
                claimStatus
              })
            }
          } else if (claimStatus === 'mfREJ' || claimStatus === 'swREJ') {
            console.log('Claim rejected for invoice:', invoice.invoiceNo)
            rejectedCount++

            // Find the original invoice to get the amount that was billed to medical aid
            const originalInvoice = await agilite.Connectors.execute(
              BillingEnums.profileKeys.PATIENT_LEDGER,
              BillingEnums.routeKeys.READ,
              {
                filter: JSON.stringify({
                  invoiceNo: invoice.invoiceNo,
                  type: BillingEnums.transactionTypes.INVOICE
                })
              }
            )

            // Use the new invoice transition function to properly handle claim rejection
            try {
              const { transitionInvoiceToPrivate } = await import('./invoiceStatusTransition')
              const transitionResult = await transitionInvoiceToPrivate(invoice._id, 'claim_rejected')

              if (transitionResult.success) {
                console.log(`Successfully transitioned rejected invoice ${invoice.invoiceNo} to private`)
              } else {
                console.error(`Failed to transition rejected invoice ${invoice.invoiceNo}:`, transitionResult.message)
              }
            } catch (error) {
              console.error(`Error transitioning rejected invoice ${invoice.invoiceNo}:`, error)

              // Fallback to original logic if transition fails
              if (originalInvoice.data && originalInvoice.data.length > 0) {
                const billedMedicalAidAmount = originalInvoice.data[0].medicalAidAmount || 0
                if (billedMedicalAidAmount > 0) {
                  await createLedgerTransaction({
                    patientId: invoice.userRef,
                    billingId: originalInvoice.data[0].billingId,
                    date: new Date(),
                    type: BillingEnums.transactionTypes.ADJUSTMENT,
                    description: `Claim rejected for Invoice ${invoice.invoiceNo}`,
                    invoiceNo: invoice.invoiceNo,
                    debit: 0,
                    credit: 0,
                    medicalAidAmount: -billedMedicalAidAmount,
                    patientAmount: billedMedicalAidAmount,
                    notes: `Full medical aid portion of R${billedMedicalAidAmount.toFixed(
                      2
                    )} moved to patient due to claim rejection.`
                  })
                }
              }
            }

            syncResults.push({
              invoiceNo: invoice.invoiceNo,
              status: 'rejected',
              reason: claimStatus
            })
          } else {
            // Other status (pending, etc)
            pendingCount++
            syncResults.push({
              invoiceNo: invoice.invoiceNo,
              status: 'pending',
              claimStatus: claimStatus || 'unknown'
            })
          }
        } else {
          // No response data
          syncResults.push({
            invoiceNo: invoice.invoiceNo,
            status: 'no_response'
          })
        }
      } catch (error) {
        console.error('Error checking payment for invoice', invoice.invoiceNo, ':', error)
        syncResults.push({
          invoiceNo: invoice.invoiceNo,
          status: 'error',
          error: error.message
        })
      }
    }

    const syncedCount = syncResults.filter((r) => r.status === 'synced').length

    // Build detailed message
    let message = ''
    if (syncedCount > 0) {
      message = `Synced ${syncedCount} new medical aid payment(s). `
    }
    if (alreadyPaidCount > 0) {
      message += `${alreadyPaidCount} invoice(s) already have payments recorded. `
    }
    if (pendingCount > 0) {
      message += `${pendingCount} claim(s) still pending. `
    }
    if (rejectedCount > 0) {
      message += `${rejectedCount} claim(s) rejected. `
    }

    if (!message) {
      message = 'No medical aid payments to process'
    }

    console.log('Sync complete. Results:', syncResults)

    return {
      success: true,
      syncedPayments: syncedCount,
      alreadyPaid: alreadyPaidCount,
      pending: pendingCount,
      rejected: rejectedCount,
      totalChecked: invoices.length,
      results: syncResults,
      message: message.trim()
    }
  } catch (error) {
    console.error('Error syncing medical aid payments:', error)
    throw error
  }
}

export default {
  processInvoiceCreation,
  processPatientPayment,
  processMedicalAidRemittance,
  processRefund,
  processCreditNote,
  getComprehensiveBalance,
  syncMedicalAidPayments
}
